
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Badge as BadgeType } from '@/types/quests';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Image } from 'lucide-react';

interface BadgesListProps {
  badges: BadgeType[];
  loading: boolean;
  onEdit: (badge: BadgeType) => void;
  onDelete: (badgeId: string) => void;
}

const BadgesList: React.FC<BadgesListProps> = ({ badges, loading, onEdit, onDelete }) => {
  const { t } = useTranslationUtils();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (badges.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {t('admin.noBadgesFound')}
      </div>
    );
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead>{t('admin.badgeName')}</TableHead>
            <TableHead>{t('admin.description')}</TableHead>
            <TableHead className="text-right">{t('admin.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {badges.map((badge) => (
            <TableRow key={badge.id}>
              <TableCell>
                {badge.image_url ? (
                  <img 
                    src={badge.image_url} 
                    alt={badge.name} 
                    className="w-10 h-10 object-cover rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <Image className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="font-medium">{badge.name}</div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-muted-foreground line-clamp-2">
                  {badge.description || t('admin.noDescription')}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(badge)}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">{t('admin.edit')}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(badge.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">{t('admin.delete')}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default BadgesList;
